'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type { StoreAdminsPanelUser } from './store-user-row'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@mentora/ui-components'
import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { StoreUserRow } from './store-user-row'

export interface StoreAdminsPanelActions {
  setStoreAdmin: (input: { userId: string, grant: boolean }) => Promise<ActionResult>
}

export interface StoreAdminsPanelProps {
  staff: StoreAdminsPanelUser[]
  results: StoreAdminsPanelUser[]
  query: string
  /** Shortest query the server searches; mirrors `MIN_SEARCH_LENGTH`. */
  minSearchLength: number
  currentUserId: string
  actions: StoreAdminsPanelActions
}

interface PendingChange {
  user: StoreAdminsPanelUser
  grant: boolean
}

export function StoreAdminsPanel({ staff, results, query, minSearchLength, currentUserId, actions }: StoreAdminsPanelProps) {
  const t = useTranslations('storeAdmins')
  const tCommon = useTranslations('common')
  const { showError, showSuccess } = useActionToast()
  const [isPending, startTransition] = useTransition()
  const [pending, setPending] = useState<PendingChange | null>(null)
  const searched = query.length >= minSearchLength

  function confirm() {
    if (!pending)
      return
    const { user, grant } = pending
    startTransition(async () => {
      const result = await actions.setStoreAdmin({ userId: user.id, grant })
      setPending(null)
      if (!result.ok) {
        showError(result)
        return
      }
      showSuccess(t(grant ? 'toasts.granted' : 'toasts.revoked', { name: user.name }))
    })
  }

  const onChange = (user: StoreAdminsPanelUser, grant: boolean) => setPending({ user, grant })

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-8">
        <div>
          <h1 className="font-(--font-display) text-3xl tracking-tight text-foreground">{t('title')}</h1>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('staff.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {staff.length === 0
              ? <p className="text-sm text-muted-foreground">{t('staff.empty')}</p>
              : (
                  <ul className="divide-y divide-border" data-testid="store-admins-staff">
                    {staff.map(user => (
                      <StoreUserRow key={user.id} user={user} isCurrentUser={user.id === currentUserId} disabled={isPending} onChange={onChange} />
                    ))}
                  </ul>
                )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('search.title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* A plain GET form: the query lives in the URL and the server searches. */}
            <form method="get" className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="store-admins-query">{t('search.label')}</Label>
                <Input id="store-admins-query" name="q" type="search" defaultValue={query} placeholder={t('search.placeholder')} />
              </div>
              <Button type="submit" variant="outline">{t('search.submit')}</Button>
            </form>
            {!searched && <p className="text-sm text-muted-foreground">{t('search.hint', { count: minSearchLength })}</p>}
            {searched && results.length === 0 && <p className="text-sm text-muted-foreground">{t('search.empty')}</p>}
            {results.length > 0 && (
              <ul className="divide-y divide-border" data-testid="store-admins-results">
                {results.map(user => (
                  <StoreUserRow key={user.id} user={user} isCurrentUser={user.id === currentUserId} disabled={isPending} onChange={onChange} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!pending} onOpenChange={open => !open && !isPending && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(pending?.grant ? 'grantDialog.title' : 'revokeDialog.title')}</DialogTitle>
            <DialogDescription>
              {t(pending?.grant ? 'grantDialog.description' : 'revokeDialog.description', { name: pending?.user.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={isPending}>
              {tCommon('cancel')}
            </Button>
            <Button onClick={confirm} disabled={isPending}>
              {isPending ? t('saving') : t(pending?.grant ? 'grant' : 'revoke')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
